import { supabase } from '@/integrations/supabase/client';

export interface SEOMetricInsert {
  url: string;
  page_title?: string;
  meta_description?: string;
  h1_count?: number;
  meta_keywords_count?: number;
  images_without_alt?: number;
  total_images?: number;
  internal_links?: number;
  external_links?: number;
  word_count?: number;
  readability_score?: number;
  core_web_vitals?: Record<string, any>;
  structured_data?: Record<string, any>;
  seo_score?: number;
  issues?: any[];
  recommendations?: any[];
}

export interface SEOIssueInsert {
  url: string;
  issue_type: string;
  severity: 'critical' | 'warning' | 'info';
  category: string;
  title: string;
  description: string;
  fix_suggestion?: string;
  status?: 'open' | 'in_progress' | 'resolved' | 'ignored';
}

export const seoDbService = {
  async saveMetrics(rows: SEOMetricInsert[]) {
    if (!rows.length) return { data: null, error: null };
    
    try {
      // Clean and validate data before insert
      const cleanRows = rows.map(row => ({
        ...row,
        url: row.url?.substring(0, 500) || '',
        page_title: row.page_title?.substring(0, 255) || null,
        meta_description: row.meta_description?.substring(0, 500) || null,
        seo_score: Math.max(0, Math.min(100, row.seo_score || 0)),
        h1_count: Math.max(0, row.h1_count || 0),
        meta_keywords_count: Math.max(0, row.meta_keywords_count || 0),
        images_without_alt: Math.max(0, row.images_without_alt || 0),
        total_images: Math.max(0, row.total_images || 0),
        internal_links: Math.max(0, row.internal_links || 0),
        external_links: Math.max(0, row.external_links || 0),
        word_count: Math.max(0, row.word_count || 0),
        readability_score: Math.max(0, Math.min(100, row.readability_score || 0)),
        core_web_vitals: row.core_web_vitals || null,
        structured_data: row.structured_data || null,
        issues: row.issues || [],
        recommendations: row.recommendations || []
      }));

      const { data, error } = await supabase.from('seo_metrics').insert(cleanRows);
      if (error) {
        console.error('SEO Metrics insert error:', error);
      }
      return { data, error };
    } catch (err) {
      console.error('SEO Metrics save exception:', err);
      return { data: null, error: err };
    }
  },

  async saveIssues(rows: SEOIssueInsert[]) {
    if (!rows.length) return { data: null, error: null };
    
    try {
      // Clean and validate issues data
      const cleanRows = rows.map(row => ({
        url: row.url?.substring(0, 500) || '',
        issue_type: row.issue_type?.substring(0, 100) || 'unknown',
        severity: ['critical', 'warning', 'info'].includes(row.severity) ? row.severity : 'info',
        category: row.category?.substring(0, 100) || 'general',
        title: row.title?.substring(0, 255) || 'SEO Issue',
        description: row.description?.substring(0, 500) || '',
        fix_suggestion: row.fix_suggestion?.substring(0, 500) || null,
        status: 'open' as const
      }));

      const { data, error } = await supabase.from('seo_issues').insert(cleanRows);
      if (error) {
        console.error('SEO Issues insert error:', error);
      }
      return { data, error };
    } catch (err) {
      console.error('SEO Issues save exception:', err);
      return { data: null, error: err };
    }
  },

  async saveSchemaValidation(result: {
    url: string;
    schema_type: string;
    schema_content: Record<string, any>;
    validation_status: 'valid' | 'invalid' | 'warning';
    validation_errors?: any[];
    validation_warnings?: any[];
    google_structured_data_test?: Record<string, any> | null;
  }) {
    const { data, error } = await supabase.from('schema_validation_results').insert([result]);
    return { data, error };
  },

  async getDashboardSummary() {
    // Issues counts
    const [openIssues, criticalIssues] = await Promise.all([
      supabase.from('seo_issues').select('*', { count: 'exact', head: true }).eq('status', 'open'),
      supabase.from('seo_issues').select('*', { count: 'exact', head: true }).eq('status', 'open').eq('severity', 'critical'),
    ]);

    // Recent metrics for average score
    const { data: recentMetrics } = await supabase
      .from('seo_metrics')
      .select('seo_score, created_at, url')
      .order('created_at', { ascending: false })
      .limit(200);

    const avgScore = recentMetrics && recentMetrics.length
      ? Math.round(recentMetrics.reduce((s, r) => s + (r.seo_score || 0), 0) / recentMetrics.length)
      : 0;

    return {
      openIssues: openIssues.count || 0,
      criticalIssues: criticalIssues.count || 0,
      avgScore,
      pagesAnalyzed: recentMetrics?.length || 0,
    };
  },
};
